import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { api, ClientApiError } from '../api'
import { useAuth } from '../AuthProvider'
import { useAppPath } from '../nav'
import { useWorkspaceAccount } from '../WorkspaceAccountProvider'
import { tradingServerLabel } from '../branding'
import { maskLogin } from '../safe-ui'
import { Button } from '../../components/ui/Button'

function initials(name: string, email?: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase()
  if (parts.length === 1 && parts[0]) return parts[0].slice(0, 2).toUpperCase()
  const local = (email || '').split('@')[0] || 'PP'
  return local.slice(0, 2).toUpperCase()
}

function safePasswordError(err: unknown, fallback: string): string {
  if (err instanceof ClientApiError) {
    const msg = (err.message || '').trim()
    if (!msg) return fallback
    const lower = msg.toLowerCase()
    if (lower.includes('internal') || lower.includes('traceback') || lower.includes('stack')) {
      return fallback
    }
    return msg
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function isStrongTradingPassword(value: string): boolean {
  return (
    value.length >= 8 &&
    value.length <= 32 &&
    /[A-Z]/.test(value) &&
    /[a-z]/.test(value) &&
    /\d/.test(value) &&
    /[!@#$%^&*\-_=+]/.test(value)
  )
}

export function ProfilePage() {
  const { logout, isAuthenticated, client } = useAuth()
  const appPath = useAppPath()
  const ws = useWorkspaceAccount()

  const displayName =
    [client?.firstName, client?.lastName].filter(Boolean).join(' ') ||
    (client?.email || '').split('@')[0] ||
    'Client'
  const email = client?.email || '—'
  const verified = Boolean(client?.emailVerified)
  const clientRef = client?.id != null ? String(client.id) : '—'
  const live =
    ws.accounts.find((a) => {
      const m = String(a.mode || '').toUpperCase()
      return m === 'LIVE' || m === 'REAL'
    }) || null
  const demo =
    ws.accounts.find((a) => String(a.mode || '').toUpperCase() === 'DEMO') || null

  const tradingOptions = useMemo(() => {
    const rows: { id: number; label: string; mode: string }[] = []
    if (live) {
      rows.push({
        id: live.id,
        mode: 'LIVE',
        label: `Live · ${maskLogin(live.mt5Login)} · ${tradingServerLabel(live.mode, live.server)}`,
      })
    }
    if (demo) {
      rows.push({
        id: demo.id,
        mode: 'DEMO',
        label: `Demo · ${maskLogin(demo.mt5Login)} · ${tradingServerLabel(demo.mode, demo.server)}`,
      })
    }
    return rows
  }, [live, demo])

  const [portalCurrent, setPortalCurrent] = useState('')
  const [portalNew, setPortalNew] = useState('')
  const [portalConfirm, setPortalConfirm] = useState('')
  const [portalBusy, setPortalBusy] = useState(false)
  const [portalError, setPortalError] = useState('')
  const [portalOk, setPortalOk] = useState('')

  const [mt5AccountId, setMt5AccountId] = useState<number | ''>('')
  const [mt5Current, setMt5Current] = useState('')
  const [mt5New, setMt5New] = useState('')
  const [mt5Confirm, setMt5Confirm] = useState('')
  const [mt5Busy, setMt5Busy] = useState(false)
  const [mt5Error, setMt5Error] = useState('')
  const [mt5Ok, setMt5Ok] = useState('')

  useEffect(() => {
    if (!mt5AccountId && tradingOptions[0]) {
      setMt5AccountId(tradingOptions[0].id)
    }
  }, [tradingOptions, mt5AccountId])

  const selectedMt5 = tradingOptions.find((o) => o.id === mt5AccountId) || null
  const mt5NeedsCurrent = selectedMt5?.mode === 'LIVE'

  async function submitPortalPassword(e: FormEvent) {
    e.preventDefault()
    setPortalError('')
    setPortalOk('')
    if (portalNew.length < 8) {
      setPortalError('New portal password must be at least 8 characters.')
      return
    }
    if (portalNew !== portalConfirm) {
      setPortalError('New portal password confirmation does not match.')
      return
    }
    setPortalBusy(true)
    try {
      const res = await api.changePortalPassword({
        currentPassword: portalCurrent,
        newPassword: portalNew,
        confirmPassword: portalConfirm,
      })
      setPortalOk(res.message || 'Portal password updated.')
      setPortalCurrent('')
      setPortalNew('')
      setPortalConfirm('')
    } catch (err) {
      setPortalError(safePasswordError(err, 'Unable to update portal password.'))
    } finally {
      setPortalBusy(false)
    }
  }

  async function submitMt5Password(e: FormEvent) {
    e.preventDefault()
    setMt5Error('')
    setMt5Ok('')
    if (!mt5AccountId) {
      setMt5Error('Select a trading account.')
      return
    }
    if (!isStrongTradingPassword(mt5New)) {
      setMt5Error(
        'MT5 password needs 8–32 chars with upper, lower, digit, and a symbol (!@#$%^&*-_=+).',
      )
      return
    }
    if (mt5New !== mt5Confirm) {
      setMt5Error('New MT5 password confirmation does not match.')
      return
    }
    if (mt5NeedsCurrent && !mt5Current) {
      setMt5Error('Current MT5 trading password is required for Live accounts.')
      return
    }
    setMt5Busy(true)
    try {
      const res = await api.changeTradingPassword(Number(mt5AccountId), {
        newPassword: mt5New,
        confirmPassword: mt5Confirm,
        currentPassword: mt5NeedsCurrent ? mt5Current : undefined,
      })
      setMt5Ok(res.message || 'MT5 trading password updated.')
      setMt5Current('')
      setMt5New('')
      setMt5Confirm('')
    } catch (err) {
      setMt5Error(safePasswordError(err, 'Unable to update MT5 trading password.'))
    } finally {
      setMt5Busy(false)
    }
  }

  return (
    <section className="cp-page cp-profile">
      <header className="cp-profile__head">
        <div className="cp-profile__identity">
          <div className="cp-profile__avatar" aria-hidden>
            {initials(displayName, client?.email)}
          </div>
          <div>
            <h1>{displayName}</h1>
            <p className="cp-profile__email">{email}</p>
          </div>
        </div>
        <div className="cp-profile__head-actions">
          <div className="cp-profile__badges">
            {verified ? (
              <span className="cp-badge cp-badge--status">Verified</span>
            ) : (
              <span className="cp-chip cp-chip--warn">Email not verified</span>
            )}
            <span className={`cp-chip${isAuthenticated ? '' : ' cp-chip--warn'}`}>
              {isAuthenticated ? 'Session active' : 'Signed out'}
            </span>
            <span className="cp-chip">
              Ref <strong className="cp-num">{clientRef}</strong>
            </span>
          </div>
          <Button variant="secondary" size="sm" onClick={logout}>
            Logout
          </Button>
        </div>
      </header>

      <div className="cp-profile__duo">
        <section className="cp-profile__panel">
          <div className="cp-profile__panel-head">
            <h2>Account</h2>
            <p className="cp-muted">Portal identity and linked MT5 accounts.</p>
          </div>

          <dl className="cp-profile__grid">
            <div>
              <dt>Full name</dt>
              <dd>{displayName}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{email}</dd>
            </div>
            <div>
              <dt>Client reference</dt>
              <dd className="cp-num">{clientRef}</dd>
            </div>
            <div>
              <dt>Verification</dt>
              <dd>{verified ? 'Email verified' : 'Pending verification'}</dd>
            </div>
            <div>
              <dt>Portal status</dt>
              <dd>{isAuthenticated ? 'Active' : 'Signed out'}</dd>
            </div>
            <div>
              <dt>Workspace</dt>
              <dd>
                {ws.hasLiveAccount ? 'Live ready' : 'Live not created'}
                {demo ? ' · Demo available' : ''}
              </dd>
            </div>
          </dl>

          <div className="cp-profile__mt5-block">
            <div className="cp-profile__mt5-head">
              <h3>Trading accounts</h3>
              <Button to={appPath('/app/accounts')} variant="ghost" size="sm">
                Manage
              </Button>
            </div>
            <ul className="cp-profile__accounts">
              <li>
                <span className="cp-profile__acct-mode">Live</span>
                {live ? (
                  <strong>
                    {maskLogin(live.mt5Login)} · {tradingServerLabel(live.mode, live.server)}
                  </strong>
                ) : (
                  <span className="cp-muted">Not created yet</span>
                )}
              </li>
              <li>
                <span className="cp-profile__acct-mode">Demo</span>
                {demo ? (
                  <strong>
                    {maskLogin(demo.mt5Login)} · {tradingServerLabel(demo.mode, demo.server)}
                  </strong>
                ) : (
                  <span className="cp-muted">Not created yet</span>
                )}
              </li>
            </ul>
          </div>
        </section>

        <section className="cp-profile__panel cp-profile__security" aria-labelledby="profile-security-heading">
          <div className="cp-profile__panel-head">
            <h2 id="profile-security-heading">Security</h2>
            <p className="cp-muted">Update portal and MT5 passwords. Never share them.</p>
          </div>

          <div className="cp-profile__security-grid">
            <form className="cp-profile__pw-form" onSubmit={(e) => void submitPortalPassword(e)}>
              <h3>Portal password</h3>
              <p className="cp-note">New password must be at least 8 characters.</p>
              <label className="cp-field">
                Current password
                <input
                  type="password"
                  autoComplete="current-password"
                  value={portalCurrent}
                  onChange={(e) => setPortalCurrent(e.target.value)}
                  required
                />
              </label>
              <label className="cp-field">
                New password
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={portalNew}
                  onChange={(e) => setPortalNew(e.target.value)}
                  required
                />
              </label>
              <label className="cp-field">
                Confirm new password
                <input
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={portalConfirm}
                  onChange={(e) => setPortalConfirm(e.target.value)}
                  required
                />
              </label>
              {portalError ? (
                <p className="cp-profile__pw-msg cp-profile__pw-msg--error" role="alert">
                  {portalError}
                </p>
              ) : null}
              {portalOk ? <p className="cp-profile__pw-msg cp-profile__pw-msg--ok">{portalOk}</p> : null}
              <Button type="submit" variant="primary" size="sm" disabled={portalBusy}>
                {portalBusy ? 'Updating…' : 'Update portal password'}
              </Button>
            </form>

            <form className="cp-profile__pw-form" onSubmit={(e) => void submitMt5Password(e)}>
              <h3>MT5 trading password</h3>
              <p className="cp-note">
                {'8–32 characters with uppercase, lowercase, a digit, and a symbol (!@#$%^&*-_=+).'}
              </p>
              {tradingOptions.length === 0 ? (
                <p className="cp-muted">Create a trading account first.</p>
              ) : (
                <>
                  <label className="cp-field">
                    Trading account
                    <select
                      value={mt5AccountId}
                      onChange={(e) => {
                        setMt5AccountId(e.target.value ? Number(e.target.value) : '')
                        setMt5Current('')
                        setMt5Error('')
                        setMt5Ok('')
                      }}
                      required
                    >
                      {tradingOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {mt5NeedsCurrent ? (
                    <label className="cp-field">
                      Current MT5 password
                      <input
                        type="password"
                        autoComplete="off"
                        value={mt5Current}
                        onChange={(e) => setMt5Current(e.target.value)}
                        required
                      />
                    </label>
                  ) : (
                    <p className="cp-note">Demo can set a new password without the previous one.</p>
                  )}
                  <label className="cp-field">
                    New MT5 password
                    <input
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      maxLength={32}
                      value={mt5New}
                      onChange={(e) => setMt5New(e.target.value)}
                      required
                    />
                  </label>
                  <label className="cp-field">
                    Confirm MT5 password
                    <input
                      type="password"
                      autoComplete="new-password"
                      minLength={8}
                      maxLength={32}
                      value={mt5Confirm}
                      onChange={(e) => setMt5Confirm(e.target.value)}
                      required
                    />
                  </label>
                  {mt5Error ? (
                    <p className="cp-profile__pw-msg cp-profile__pw-msg--error" role="alert">
                      {mt5Error}
                    </p>
                  ) : null}
                  {mt5Ok ? <p className="cp-profile__pw-msg cp-profile__pw-msg--ok">{mt5Ok}</p> : null}
                  <Button type="submit" variant="primary" size="sm" disabled={mt5Busy}>
                    {mt5Busy ? 'Updating…' : 'Update MT5 password'}
                  </Button>
                </>
              )}
            </form>
          </div>
        </section>
      </div>
    </section>
  )
}
